import React, { useState, useEffect } from "react";
import { Link, useHistory } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import {
    Form,
    FormGroup,
    Label,
    InputGroup,
    InputGroupText,
    InputGroupAddon,
    FormFeedback,
    Row,
    Col
} from 'reactstrap'
import { useFormContext } from 'react-hook-form';
import { useMutation } from 'react-query';
import * as Yup from 'yup';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import InputWithLoader from '../../components/InputWithLoader';

const AddressSchema = Yup.object().shape({
  zipCode: Yup.string()
  .required('Required'),
  street: Yup.string()
    .required('Required'),
  exteriorNumber: Yup.string()
    .required('Required'),
  interiorNumber: Yup.string(),
  state: Yup.string()
    .required('Required'),
  municipio: Yup.string()
    .required('Required'),
})


export const HeirSchema = Yup.object().shape({
  name: Yup.string().required('Required'),
  last_name: Yup.string().required('Required'),
  email: Yup.string()
      .email('InvalidEmail')
      .required('Required'),  
  properties: Yup.object().shape({
    percentageOfInheritanceState: Yup.string()
    .required('Required'),    
  })
});
export const LegateeSchema = Yup.object().shape({
  name: Yup.string().required('Required'),
  last_name: Yup.string().required('Required'),
  email: Yup.string()
      .email('InvalidEmail')
      .required('Required'),  
  properties: Yup.object().shape({
    curp: Yup.string()
    .required('Required'),    
    legacyDescription: Yup.string()
    .required('Required'),    
  })
});
export const WitnessSchema = Yup.object().shape({
  name: Yup.string().required('Required'),
  last_name: Yup.string().required('Required'),
  email: Yup.string()
      .email('InvalidEmail')
      .required('Required'),  
  properties: Yup.object().shape({
    curp: Yup.string()
    .required('Required'),  
    maritalStatus: Yup.string()
        .required('Required'), 
    address: AddressSchema,
    nationality: Yup.string()
        .required('Required'),
    birthDate: Yup.string()
        .required('Required'),
  })
});


export const TestatorSchema = Yup.object().shape({
    name: Yup.string().required('Required'),
    last_name: Yup.string().required('Required'),
    email: Yup.string()
      .email('InvalidEmail')
      .required('Required'),  
    properties: Yup.object().shape({
      curp: Yup.string()
      .required('Required'),    
      maritalStatus: Yup.string()
        .required('Required'),
      nationality: Yup.string()
        .required('Required'),
      officialIdentificationNumber: Yup.string()
        .required('Required'),
      address: AddressSchema,
      birthDate: Yup.string()
      .required('Required'),
      birthplace: Yup.string()
      .required('Required'),
    })
});

export const ExecutorSchema = Yup.object().shape({
    name: Yup.string().required('Required'),
    last_name: Yup.string().required('Required'),
    email: Yup.string()
      .email('InvalidEmail')
      .required('Required'),  
    properties: Yup.object().shape({
      curp: Yup.string()
      .required('Required'),    
      maritalStatus: Yup.string()
        .required('Required'),
      address: AddressSchema,
    })
});

export const TestamentFormSchema = Yup.object().shape({
  testator: TestatorSchema, 
  heirs: Yup.array().of(HeirSchema),
  legatees: Yup.array().of(LegateeSchema),
  executors:  Yup.array().of(ExecutorSchema),
  witnesses: Yup.array().of(WitnessSchema)
})


const testatorInitialValue = {
  name: '',
  last_name: '',
  email: '',
  properties: {
    curp: '',    
    maritalStatus: '',
    zipCode: '',
    streetAddress: '',
    exteriorNumber: '',
    interiorNumber: '',
    birthDate: '',
    birthplace: '',
    state: '',
    municipio: '',
    nationality: '',
    officialIdentificationNumber: ''
  }
};

export const executorInitialValue = testatorInitialValue;

export const heirInitialValue = {
  name: '',
  last_name: '',
  email: '',
  properties: {
    percentageOfInheritanceState: 0,    
  }
};

export const legateeInitialValue = {
  name: '',
  last_name: '',
  email: '',
  properties: {
    curp: '',   
    legacyDescription: '' 
  }
};

export const witnessInitialValue = {
  name: '',
  last_name: '',
  email: '',
  properties: {
    curp: '',   
    maritalStatus: '',
    zipCode: '',
    streetAddress: '',
    exteriorNumber: '',
    interiorNumber: '',
    state: '',
    birthDate: '',
    municipio: '',
    nationality: '',
  }
};

export const testamentInitialValues = {
  testator: testatorInitialValue, 
  heirs: [heirInitialValue],
  legatees: [],
  executors: [],
  witnesses: [witnessInitialValue, witnessInitialValue, witnessInitialValue, witnessInitialValue, witnessInitialValue]
}


const InputMapper = {
  name: NameInput,
  lastName: LastNameInput,
  email: EmailInput,
  curp: CurpInput,
  maritalStatus: MaritalStatusInput,
  street: StreetInput,
  zipCode: ZipCodeInput,
  exteriorNumber: ExteriorNumberInput,
  interiorNumber: InteriorNumberInput,
  state: StateInput,
  municipio: MunicipioInput,
  percentageOfInheritanceState: PercentageOfInheritanceStateInput,
  legacyDescription: LegacyDescriptionInput,
  birthDate: BirthDateInput,
  nationality: NationalityInput,
  officialIdentificationNumber: OfficialIdentificationNumberInput,
  birthplace: BirthplaceInput,
}

function getKey(obj, key) {
  const keys = key.split('.');

  return keys.reduce((object, key) => {
    if (!object[key]) return object;
    return object[key]
  }, obj)
}



export function TestamentForm({ className, name, initialValue = {}, isInArray, isLoading, errors = {}, fields, showRemove, onRemove }) {
    const { t } = useTranslation('common')

    return (
      <div className={className}>
        <Row >
            {
              fields.map(field => {
                const Input = InputMapper[field];

                return <Input name={name} isInArray={isInArray} isLoading={isLoading} initialValue={initialValue} errors={errors} />
              })
            }
        </Row>
        <Row hidden={!showRemove} className="flex-row-reverse px-3 text-muted align-content-center align-items-center">
            <div className="pointer" onClick={onRemove}>
              <FontAwesomeIcon icon="trash" />  <small className="mr-1">Eliminar</small> 
            </div>
        </Row>
      </div>
    );
}


function NameInput({ name, initialValue, isInArray, isLoading, errors = {}}) {
  const { t } = useTranslation('common')
  const { register } = useFormContext();

  return (
    <Col tag={FormGroup} md={6}>
      <CustomLabel for="name" text={t('forms.givenName')} required />
      <InputGroup className="mb-2">
          <InputWithLoader 
              type="text" 
              id="name"
              aria-describedby="emailHelp" 
              autoComplete="off" 
              placeholder={t('forms.givenName')}
              loading={isLoading}
              name={`${name}.name`}
              ref={isInArray ? register() : register}
              defaultValue={initialValue.name}
              invalid={errors.name}
          />
          <FormFeedback invalid={errors.name}>{t(`errors.${errors.name?.message}`)}</FormFeedback>
      </InputGroup>
    </Col>
  )
}
function LastNameInput({ name, initialValue, isInArray, isLoading, errors = {} }) {
  const { t } = useTranslation('common')
  const { register } = useFormContext();
  
  return (
    <Col tag={FormGroup} md={6}>
      <CustomLabel for="last_name" text={t('forms.familyName')} required />
      <InputGroup className="mb-2">
          <InputWithLoader 
              type="text" 
              id="last_name"
              aria-describedby="emailHelp" 
              autoComplete="off" 
              loading={isLoading}
              placeholder={t('forms.familyName')}
              name={`${name}.last_name`}
              ref={isInArray ? register() : register}
              defaultValue={initialValue.last_name}
              invalid={errors.last_name}
          />
          <FormFeedback invalid={errors.last_name}>{t(`errors.${errors.last_name?.message}`)}</FormFeedback>
      </InputGroup>
    </Col>
  )
}
function EmailInput({ name, initialValue, isInArray, isLoading, errors = {} }) {
  const { t } = useTranslation('common')
  const { register } = useFormContext();
  
  return (
    <Col tag={FormGroup} md={6}>
      <CustomLabel for="email" text={t('forms.email')} required />
      <InputGroup className="mb-2">
          <InputWithLoader 
              type="email" 
              id="email"
              aria-describedby="emailHelp" 
              autoComplete="off" 
              loading={isLoading}
              placeholder={t('forms.email')}
              name={`${name}.email`}
              ref={isInArray ? register() : register}
              defaultValue={initialValue.email}
              invalid={errors.email}
          />
          <FormFeedback invalid={errors.email}>{t(`errors.${errors.email?.message}`)}</FormFeedback>
      </InputGroup>
    </Col>
  )
}
function PercentageOfInheritanceStateInput({ name, initialValue, isInArray, isLoading, errors = {} }) {
  const { t } = useTranslation('common')
  const { register } = useFormContext();

  console.log({errors})
  
  return (
    <Col tag={FormGroup} md={6}>
      <CustomLabel for="percentageOfInheritanceState" text={t('Porcentaje de la masa hereditaria')} required />
      <InputGroup className="mb-2">
          <InputWithLoader 
              type="number" 
              id="percentageOfInheritanceState"
              aria-describedby="emailHelp" 
              autoComplete="off" 
              min={1}
              max={100}
              loading={isLoading}
              placeholder={t('Porcentaje de la masa hereditaria')}
              name={`${name}.properties.percentageOfInheritanceState`}
              ref={isInArray ? register() : register}
              defaultValue={initialValue.properties?.percentageOfInheritanceState}
              invalid={errors.properties?.percentageOfInheritanceState}
          />
          <FormFeedback invalid={errors.properties?.percentageOfInheritanceState}>{t(`errors.${errors.properties?.percentageOfInheritanceState?.message}`)}</FormFeedback>
      </InputGroup>
    </Col>
  )
}

function CurpInput({ name, initialValue, isInArray, isLoading, errors = {} }) {
  const { t } = useTranslation('common')
  const { register } = useFormContext();
  
  return (
    <Col tag={FormGroup} md={6}>
      <CustomLabel for="curp" text={t('CURP')} required />
      <InputGroup className="mb-2">
          <InputWithLoader 
              type="text" 
              id="curp"
              aria-describedby="emailHelp" 
              autoComplete="off" 
              placeholder={t('CURP')}
              loading={isLoading}
              name={`${name}.properties.curp`}
              ref={isInArray ? register() : register}
              defaultValue={initialValue.properties?.curp}
              invalid={errors.properties?.curp}
          />
          <FormFeedback invalid={errors.properties?.curp}>{t(`errors.${errors.properties?.curp?.message}`)}</FormFeedback>
      </InputGroup>
    </Col>
  )
}

function LegacyDescriptionInput({ name, initialValue, isInArray, isLoading, errors = {} }) {
  const { t } = useTranslation('common')
  const { register } = useFormContext();
  
  return (
    <Col tag={FormGroup} md={12}>
      <CustomLabel for="legacyDescription" text={t('Descripcion del legado')} required />
      <InputGroup className="mb-2">
          <InputWithLoader 
              type="textarea"
              style={{height: 200}}
              id="legacyDescription"
              aria-describedby="emailHelp" 
              autoComplete="off" 
              loading={isLoading}
              placeholder={t('Descripcion del legado')}
              name={`${name}.properties.legacyDescription`}
              ref={isInArray ? register() : register}
              defaultValue={initialValue.properties?.legacyDescription}
              invalid={errors.properties?.legacyDescription}
          />
          <FormFeedback invalid={errors.properties?.legacyDescription}>{t(`errors.${errors.properties?.legacyDescription?.message}`)}</FormFeedback>
      </InputGroup>
    </Col>
  )
}
function MaritalStatusInput({ name, initialValue, isInArray, isLoading, errors = {} }) {
  const { t } = useTranslation('common')
  const { register } = useFormContext();
  
  return (
    <Col tag={FormGroup} md={6}>
      <CustomLabel for="maritalStatus" text={t('Estado Civil')} required />
      <InputGroup className="mb-2">
          <InputWithLoader 
              type="text" 
              id="maritalStatus" 
              name="properties.maritalStatus"
              loading={isLoading}
              placeholder={t('Estado Civil')}
              name={`${name}.properties.maritalStatus`}
              ref={isInArray ? register() : register}
              defaultValue={initialValue.properties?.maritalStatus}
              invalid={errors.properties?.maritalStatus}
          />
          <FormFeedback>{t(`errors.${errors.properties?.maritalStatus?.message}`)}</FormFeedback>
      </InputGroup>
    </Col>
  )
}
function StreetInput({ name, initialValue, isInArray, isLoading, errors = {} }) {
  const { t } = useTranslation('common')
  const { register } = useFormContext();
  
  return (
    <Col tag={FormGroup} md={6}>
      <CustomLabel for="streetAddress" text={t('Calle')} required />
      <InputGroup className="mb-2">
          <InputWithLoader 
              type="text" 
              id="streetAddress" 
              placeholder={t('Calle')} 
              loading={isLoading}
              name={`${name}.properties.address.street`}
              ref={isInArray ? register() : register}
              defaultValue={initialValue.properties?.address?.street}
              invalid={errors.properties?.address?.street}   
          />
          <FormFeedback invalid={errors.properties?.address?.street}>{t(`errors.${errors.properties?.address?.street?.message}`)}</FormFeedback>
      </InputGroup>
    </Col>
  )
}
function ExteriorNumberInput({ name, initialValue, isInArray, isLoading, errors = {} }) {
  const { t } = useTranslation('common')
  const { register } = useFormContext();
  
  return (
    <Col tag={FormGroup} md={3}>
      <CustomLabel for="exteriorNumber" text={t('Exterior')} required />
      <InputGroup className="mb-2">
          <InputWithLoader 
              type="text" 
              id="exteriorNumber" 
              placeholder={t('Exterior')} 
              loading={isLoading}
              name={`${name}.properties.address.exteriorNumber`}
              ref={isInArray ? register() : register}
              defaultValue={initialValue.properties?.address?.exteriorNumber} 
              invalid={errors.properties?.address?.exteriorNumber}   
          />
          <FormFeedback invalid={errors.properties?.address?.exteriorNumber}>{t(`errors.${errors.properties?.address?.exteriorNumber?.message}`)}</FormFeedback>
      </InputGroup>
    </Col>
  )
}
function InteriorNumberInput({ name, initialValue, isInArray, isLoading, errors = {} }) {
  const { t } = useTranslation('common')
  const { register } = useFormContext();
  
  return (
    <Col tag={FormGroup} md={3}>
      <CustomLabel for="interiorNumber" text={t('Interior')} />
      <InputGroup className="mb-2">
          <InputWithLoader 
              type="text" 
              id="interiorNumber" 
              placeholder={t('Interior')} 
              loading={isLoading}
              name={`${name}.properties.address.interiorNumber`}
              ref={isInArray ? register() : register}
              defaultValue={initialValue.properties?.address?.interiorNumber} 
              invalid={errors.properties?.address?.interiorNumber}   
          />
          <FormFeedback invalid={errors.properties?.address?.interiorNumber}>{t(`errors.${errors.properties?.address?.interiorNumber?.message}`)}</FormFeedback>
      </InputGroup>
    </Col>
  )
}

function StateInput({ name, initialValue, isInArray, isLoading, errors = {} }) {
  const { t } = useTranslation('common')
  const { register } = useFormContext();
  
  return (
    <Col tag={FormGroup} md={6}>
      <CustomLabel for="state" text={t('Estado')} required />
        <InputGroup className="mb-2">
            <InputWithLoader 
                type="text" 
                id="state" 
                placeholder={t('Estado')} 
                loading={isLoading}
                name={`${name}.properties.address.state`}
                ref={isInArray ? register() : register}
                defaultValue={initialValue.properties?.address?.state} 
                invalid={errors.properties?.address?.state}   
            />
            <FormFeedback invalid={errors.properties?.address?.state}>{t(`errors.${errors.properties?.address?.state?.message}`)}</FormFeedback>
        </InputGroup>
    </Col>
  )
}

function MunicipioInput({ name, initialValue, isInArray, isLoading, errors = {}}) {
  const { t } = useTranslation('common')
  const { register } = useFormContext();
  
  return (
    <Col tag={FormGroup} md={6}>
      <CustomLabel for="municipio" text={t('Municipio')} required />
        <InputGroup className="mb-2">
            <InputWithLoader 
                type="text" 
                id="municipio" 
                placeholder={t('Municipio')} 
                loading={isLoading}
                name={`${name}.properties.address.municipio`}
                ref={isInArray ? register() : register}
                defaultValue={initialValue.properties?.address?.municipio} 
                invalid={errors.properties?.address?.municipio}   
            />
            <FormFeedback invalid={errors.properties?.address?.municipio}>{t(`errors.${errors.properties?.address?.municipio?.message}`)}</FormFeedback>
        </InputGroup>
    </Col>
  )
}

function ZipCodeInput({ name, initialValue, isInArray, isLoading, errors = {} }) {
  const { t } = useTranslation('common')
  const { register } = useFormContext();
  
  return (
    <Col tag={FormGroup} md={6}>
      <CustomLabel for="zipCode" text={t('Codigo postal')} required />
        <InputGroup className="mb-2">
            <InputWithLoader 
                type="text" 
                id="zipCode" 
                placeholder={t('Codigo postal')} 
                loading={isLoading}
                name={`${name}.properties.address.zipCode`}
                ref={isInArray ? register() : register}
                defaultValue={initialValue.properties?.address?.zipCode} 
                invalid={errors.properties?.address?.zipCode}   
            />
            <FormFeedback invalid={errors.properties?.address?.zipCode}>{t(`errors.${errors.properties?.address?.zipCode?.message}`)}</FormFeedback>
        </InputGroup>
    </Col>
  )
}


function CustomLabel({ required, text, ...rest }) {
  return (
    <Label className="font-weight-bold" {...rest}>
      {text} 
      {required && <span className="text-accent">*</span>}
    </Label>
  )
}

function NationalityInput({ name, initialValue, isInArray, isLoading, errors = {}}) {
  const { t } = useTranslation('common')
  const { register } = useFormContext();
  
  return (
    <Col tag={FormGroup} md={6}>
      <CustomLabel for="nationality" text={t('Nacionalidad')} required />
        <InputGroup className="mb-2">
            <InputWithLoader 
                type="text" 
                id="nationality" 
                placeholder={t('Nacionalidad')} 
                loading={isLoading}
                name={`${name}.properties.nationality`}
                ref={isInArray ? register() : register}
                defaultValue={initialValue.properties?.nationality} 
                invalid={errors.properties?.nationality}   
            />
            <FormFeedback invalid={errors.properties?.nationality}>{t(`errors.${errors.properties?.nationality?.message}`)}</FormFeedback>
        </InputGroup>
    </Col>
  )
}

function OfficialIdentificationNumberInput({ name, initialValue, isInArray, isLoading, errors = {}}) {
  const { t } = useTranslation('common')
  const { register } = useFormContext();
  
  return (
    <Col tag={FormGroup} md={6}>
      <CustomLabel for="officialIdentificationNumber" text={t('Número de Identificación Oficial')} required />
        <InputGroup className="mb-2">
            <InputWithLoader 
                type="text" 
                id="officialIdentificationNumber" 
                placeholder={t('Número de Identificación Oficial')} 
                loading={isLoading}
                name={`${name}.properties.officialIdentificationNumber`}
                ref={isInArray ? register() : register}
                defaultValue={initialValue.properties?.officialIdentificationNumber} 
                invalid={errors.properties?.officialIdentificationNumber}   
            />
            <FormFeedback invalid={errors.properties?.officialIdentificationNumber}>{t(`errors.${errors.properties?.officialIdentificationNumber?.message}`)}</FormFeedback>
        </InputGroup>
    </Col>
  )
}

function BirthplaceInput({ name, initialValue, isInArray, isLoading, errors = {}}) {
  const { t } = useTranslation('common')
  const { register } = useFormContext();
  
  return (
    <Col tag={FormGroup} md={6}>
      <CustomLabel for="officialIdentificationNumber" text={t('Lugar de Origen')} required />
        <InputGroup className="mb-2">
            <InputWithLoader 
                type="text" 
                id="birthplace" 
                placeholder={t('Lugar de Origen')} 
                loading={isLoading}
                name={`${name}.properties.birthplace`}
                ref={isInArray ? register() : register}
                defaultValue={initialValue.properties?.birthplace} 
                invalid={errors.properties?.birthplace}   
            />
            <FormFeedback invalid={errors.properties?.birthplace}>{t(`errors.${errors.properties?.birthplace?.message}`)}</FormFeedback>
        </InputGroup>
    </Col>
  )
}

function BirthDateInput({ name, initialValue, isInArray, isLoading, errors = {}}) {
  const { t } = useTranslation('common')
  const { register } = useFormContext();
  
  return (
    <Col tag={FormGroup} md={6}>
      <CustomLabel for="birthDate" text={t('Fecha de Nacimiento')} required />
        <InputGroup className="mb-2">
            <InputWithLoader 
                type="date" 
                id="birthDate" 
                placeholder={t('Fecha de Nacimiento')} 
                loading={isLoading}
                name={`${name}.properties.birthDate`}
                ref={isInArray ? register() : register}
                defaultValue={initialValue.properties?.birthDate} 
                invalid={errors.properties?.birthDate}   
            />
            <FormFeedback invalid={errors.properties?.birthDate}>{t(`errors.${errors.properties?.birthDate?.message}`)}</FormFeedback>
        </InputGroup>
    </Col>
  )
}